import { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { RootReducer } from 'redux/reducers';
import { selectActiveDsTab } from 'redux/mode/reducers';
import { toggleDsPick } from 'redux/mode/actions';
import useScrollMemory from 'hooks/useScrollMemory';
import forge from 'apis/forge';
import { findIndexBy } from '../utils';
import MarkdownEditor from 'components/Widget/Input/MarkdownEditor';
import * as Base from '../styles';
import * as GalleryS from '../GalleryView/styles';
import * as S from './styles';

const SAVE_DEBOUNCE_MS = 1500;

const GuidelinesView = () => {
	const dispatch = useDispatch();
	const [selectedGuidelineIndex, setSelectedGuidelineIndex] = useState<number | null>(null);
	const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const pendingSaveRef = useRef<(() => void) | null>(null);
	const { designSystemContent, activeTab, selectedDsPicks, activeDesignSystemId } = useSelector(
		(r: RootReducer) => ({
			designSystemContent: r.mode.designSystemContent,
			activeTab: selectActiveDsTab(r.mode),
			selectedDsPicks: r.mode.selectedDsPicks,
			activeDesignSystemId: r.mode.activeDesignSystemId,
		}),
		shallowEqual
	);
	const guidelines = designSystemContent?.guidelines || [];

	const contentPath = activeTab?.contentPath;
	const scrollRef = useScrollMemory(activeTab?.id ?? null);

	useEffect(() => {
		const selectedIndex = findIndexBy(guidelines, contentPath, (g) => g.path);
		setSelectedGuidelineIndex(selectedIndex);
	}, [guidelines, contentPath]);

	const selectedGuideline =
		selectedGuidelineIndex !== null ? guidelines[selectedGuidelineIndex] : null;

	// Flush any pending save on unmount
	useEffect(() => {
		return () => {
			if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
			pendingSaveRef.current?.();
		};
	}, []);

	const onGuidelineChange = useCallback(
		(markdown: string) => {
			if (!activeDesignSystemId || !selectedGuideline) return;
			if (markdown === selectedGuideline.content) return;
			if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
			const save = () => {
				pendingSaveRef.current = null;
				forge.saveDsFiles({
					designSystemId: activeDesignSystemId,
					files: [{ path: selectedGuideline.path, content: markdown }],
				});
			};
			pendingSaveRef.current = save;
			saveTimerRef.current = setTimeout(save, SAVE_DEBOUNCE_MS);
		},
		[activeDesignSystemId, selectedGuideline]
	);

	if (guidelines.length === 0) {
		return (
			<Base.SectionBase>
				<Base.SectionHeader>
					<Base.SectionTitle>Guidelines</Base.SectionTitle>
				</Base.SectionHeader>
				<Base.SectionContent>
					<p>No guidelines available.</p>
				</Base.SectionContent>
			</Base.SectionBase>
		);
	}
	const isGuidelineSelected =
		selectedGuideline &&
		selectedDsPicks.some(
			(p) => p.kind === 'guideline' && p.guidelinePath === selectedGuideline.path
		);

	return (
		<Base.SectionBase style={{ padding: 0, display: 'flex', flexDirection: 'column' }}>
			<GalleryS.GalleryContainer>
				<GalleryS.GalleryPreviewContainer>
					{selectedGuideline ? (
						<S.ScrollWrapper ref={scrollRef}>
							<S.ContentContainer>
								<S.GuidelineHeader>
									<S.GuidelineTitle>{selectedGuideline.title}</S.GuidelineTitle>
									<Base.SelectToggle
										$selected={!!isGuidelineSelected}
										onClick={() =>
											dispatch(
												toggleDsPick({
													kind: 'guideline',
													guidelinePath: selectedGuideline.path,
												})
											)
										}
									>
										{isGuidelineSelected ? 'Selected' : 'Select'}
									</Base.SelectToggle>
								</S.GuidelineHeader>
								<MarkdownEditor
									key={selectedGuideline.path}
									content={selectedGuideline.content}
									onChange={onGuidelineChange}
								/>
							</S.ContentContainer>
						</S.ScrollWrapper>
					) : (
						<GalleryS.EmptyState>Select a guideline to view</GalleryS.EmptyState>
					)}
				</GalleryS.GalleryPreviewContainer>
			</GalleryS.GalleryContainer>
		</Base.SectionBase>
	);
};

export default GuidelinesView;
